import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Zap } from "lucide-react";
import type { GeneratedLineItem } from "@/utils/estimateCalculations";

interface QuickAddMaterialsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  lineItems: GeneratedLineItem[];
  totalSquares: number;
  onAddItems: (items: GeneratedLineItem[]) => void;
}

export function QuickAddMaterialsDialog({
  open,
  onOpenChange,
  lineItems,
  totalSquares,
  onAddItems,
}: QuickAddMaterialsDialogProps) {
  const [selectedIds, setSelectedIds] = useState<Set<string>>(
    new Set(lineItems.map((item) => item.product_id))
  );

  const toggleSelection = (productId: string) => {
    const newSelected = new Set(selectedIds);
    if (newSelected.has(productId)) {
      newSelected.delete(productId);
    } else {
      newSelected.add(productId);
    }
    setSelectedIds(newSelected);
  };

  const handleAddSelected = () => {
    const selectedItems = lineItems.filter((item) => selectedIds.has(item.product_id));
    onAddItems(selectedItems);
    onOpenChange(false);
  };

  const formatQuantity = (qty: number, unit: string) => {
    if (unit === "lf" || unit === "linear feet") {
      return `${qty.toFixed(0)} LF`;
    } else if (unit === "square" || unit === "sq") {
      return `${qty.toFixed(2)} sq`;
    } else if (unit === "each" || unit === "ea") {
      return `${qty.toFixed(0)} ea`;
    }
    return `${qty} ${unit}`;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            Quick Add Roofing Materials
          </DialogTitle>
          <DialogDescription>
            Based on measurements: <strong>{totalSquares.toFixed(2)} squares</strong>
            <br />
            Select materials to add to estimate. Quantities are auto-calculated.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3 py-4">
          {lineItems.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              <p>No products available in the price book.</p>
              <p className="text-sm mt-2">
                Please ensure products are configured in the Price Book Manager.
              </p>
            </div>
          ) : (
            lineItems.map((item) => (
              <div
                key={item.product_id}
                className="flex items-center justify-between p-4 border rounded-lg hover:bg-accent/50 transition-colors"
              >
                <div className="flex items-center gap-3 flex-1">
                  <Checkbox
                    checked={selectedIds.has(item.product_id)}
                    onCheckedChange={() => toggleSelection(item.product_id)}
                    id={item.product_id}
                  />
                  <Label
                    htmlFor={item.product_id}
                    className="flex-1 cursor-pointer space-y-1"
                  >
                    <div className="font-medium">{item.description}</div>
                    {item.calculation_note && (
                      <div className="text-xs text-muted-foreground">
                        {item.calculation_note}
                      </div>
                    )}
                  </Label>
                </div>
                <div className="text-right space-y-1">
                  <Badge variant="secondary" className="font-mono">
                    {formatQuantity(item.quantity, item.unit)}
                  </Badge>
                  {item.unit_price > 0 && (
                    <div className="text-sm text-muted-foreground">
                      ${item.line_total.toFixed(2)}
                    </div>
                  )}
                </div>
              </div>
            ))
          )}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleAddSelected}
            disabled={selectedIds.size === 0}
          >
            Add Selected Items ({selectedIds.size})
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
